import type { Logger } from "@expert/logging";
import type { Channel } from "@expert/shared-types";

import { useEffect, useMemo, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import type {
    GaiaSubscription,
    GaiaSubscriptionBody,
    GaiaUnsubscription,
    GaiaWebSocketResponse,
    GaiaWebsocketContext,
} from "../types";

import { useGaiaStore } from "../../store";
import { GAIA_WS_EMITTER_NAME, gaiaWsEventBus } from "../gaiaEventBus";
import { GaiaWebSocketEvent } from "../types";
import { useGaiaStatusEvents } from "./useGaiaStatusEvents";

const heartbeatInterval = 20000;
const heartbeatTimeout = 90000;

interface GaiaWebsocket {
    baseUrl: string;
    application: string;
    token: string;
    identity: string;
    logger: Logger;
}

export function useGaiaWebsocketInternal({ baseUrl, application, token, identity, logger: loggerProp }: GaiaWebsocket) {
    const [loading, setLoading] = useState(true);
    const url = `${baseUrl}?application=${application}&token=${token}`;
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaWebsocket", supportTeam: "solve" }), [loggerProp]);

    const { sendJsonMessage, readyState } = useWebSocket(
        url,
        {
            heartbeat: {
                interval: heartbeatInterval,
                timeout: heartbeatTimeout,
                message: JSON.stringify({
                    action: "heartbeat",
                    correlationId: identity,
                }),
            },
            shouldReconnect: (_closeEvent) => true,
            reconnectAttempts: 10,
            reconnectInterval: 2000,
            onMessage: createOnMessageEventHandler(setLoading, logger),
            onError: (event: Event) => onError(event, logger),
        },
        true,
    );

    useGaiaStatusEvents(logger);

    useEffect(() => {
        if (!loading && readyState === ReadyState.CONNECTING) {
            logger.debug("GAIA | websocket connecting");
            setLoading(true);
        }
    }, [loading, readyState, logger]);

    useEffect(() => {
        if (loading && readyState === ReadyState.OPEN) {
            logger.info("GAIA | websocket open");
            sendJsonMessage({
                action: "authorize",
                token,
                correlationId: identity,
            });
            logger.debug("GAIA | authorize message sent");
        }
    }, [loading, readyState, identity, token, sendJsonMessage, logger]);

    useEffect(() => {
        if (!loading && readyState === ReadyState.CLOSED) {
            logger.info("GAIA | websocket closed");
            setLoading(false);
        }
    }, [loading, readyState, logger]);

    const subscriptionResult: GaiaWebsocketContext = useMemo(() => {
        return {
            sendJsonMessage,
            loading,
            subscribeSessionToGaia: subscribeSessionToGaia(identity),
            unsubscribeSessionFromGaia: unsubscribeSessionFromGaia(identity),
        };
    }, [sendJsonMessage, loading, identity]);

    return subscriptionResult;
}

const createOnMessageEventHandler =
    (onAuthenticated: (isLoading: boolean) => void, loggerProp: Logger) => (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data as string) as GaiaWebSocketResponse;

            // Guard against malformed messages
            if (!data?.name) {
                loggerProp.warn({ data }, "GAIA | Received message without name property");
                return;
            }

            const { body } = data;
            const logger = getLoggerContextFromBody(body, loggerProp);

            if (data.name.includes("error")) {
                // TODO: @Soluto-Private/expert-workspace-solve:we should filter out common known errors. and make it error if not known
                logger.warn({ body, eventName: data.name }, "GAIA | message received");
            } else {
                logger.debug({ body }, `GAIA | message received: ${GAIA_WS_EMITTER_NAME}_${data.name}`);
            }

            gaiaWsEventBus.emit(`${GAIA_WS_EMITTER_NAME}_${data.name}`, data);
            if (data.name === GaiaWebSocketEvent.AuthorizationSuccess) {
                onAuthenticated(false);
            }
        } catch (error: unknown) {
            loggerProp.error({ error, ...event }, "GAIA | message handler error");
        }
    };

const onError = (event: Event, logger: Logger) => {
    logger.warn({ error: event }, "GAIA | websocket error");
};

export const subscribeSessionToGaia =
    (identity: string) =>
    ({
        chatId,
        sessionId,
        partner,
        sendJsonMessage,
        callSid,
        timeout = 5000,
        logger: loggerProp,
        explicitSubscriptions,
    }: GaiaSubscription) => {
        useGaiaStore.getState().setPartner(sessionId, partner);

        // Add partner, sessionId, and chatId/callSid to logger context for session-specific logs
        const logger = loggerProp.child({
            module: "useGaiaWebsocket",
            sessionId,
            partner,
            ...(chatId && { chatId }),
            ...(callSid && { callSid }),
        });

        return new Promise<void>((resolve, reject) => {
            if (chatId) {
                useGaiaStore.getState().setSessionType(sessionId, "chat");
            } else if (callSid) {
                useGaiaStore.getState().setSessionType(sessionId, "voice");
            }

            sendJsonMessage({
                action: "subscribe",
                sessionGroupId: sessionId,
                sessionId: chatId ?? callSid ?? sessionId,
                explicitSubscriptions,
                partner,
                correlationId: identity,
            });
            logger.debug("GAIA | session subscribing");

            const timer = setTimeout(() => {
                cleanupEventListener();
                const message = "GAIA | session subscription timed out";
                logger.warn(message);
                reject(Error(message));
            }, timeout);

            const cleanupEventListener = gaiaWsEventBus.once("gaia_ws_session-subscription-success", () => {
                clearTimeout(timer);
                resolve();
            });
        });
    };

const unsubscribeSessionFromGaia =
    (identity: string) =>
    ({ sessionId, sendJsonMessage, logger }: GaiaUnsubscription) => {
        const partner = useGaiaStore.getState().partners[sessionId];

        sendJsonMessage({
            action: "unsubscribe",
            correlationId: identity,
            sessionId,
        });

        logger
            .child({
                sessionId,
                module: "useGaiaWebsocket",
                partner,
            })
            .debug("GAIA | session unsubscribing");
    };

const getLoggerContextFromBody = (body: unknown, logger: Logger) => {
    if (!body) return logger;

    const { sessionGroupId, sessionId: sessionIdFromBody } = body as Partial<GaiaSubscriptionBody>;
    if (sessionGroupId ?? sessionIdFromBody) {
        /* The sessionGroupId is a term used by GAIA. The value being used is the sessionId.
                In the event there is a call or chat, sessionId is the callSid or chatId respectively. It's stupid, we know. */

        const isThereACallSidOrChatId = sessionGroupId && sessionIdFromBody && sessionIdFromBody !== sessionGroupId;
        const callSidOrChatId = isThereACallSidOrChatId ? sessionIdFromBody : undefined;
        const sessionId = sessionGroupId ?? sessionIdFromBody;
        const partner = sessionId ? useGaiaStore.getState().partners[sessionId] : undefined;

        const sessionType = sessionId ? getSessionType(sessionId) : undefined;
        const isChat = sessionType === "chat";

        return logger.child({
            sessionId,
            ...(callSidOrChatId && isChat
                ? { chatId: callSidOrChatId }
                : callSidOrChatId
                  ? { callSid: callSidOrChatId }
                  : {}),
            partner,
        });
    }

    return logger;
};

export function getSessionType(sessionId: string): Channel | undefined {
    return useGaiaStore.getState().getSessionType(sessionId);
}
